/**
 * ListPickerModal - Modal for selecting a list to insert into a question
 */
import React, { useState, useMemo } from "react"
import { Modal, Input, List, Tag, Empty } from "antd"
import {
  SearchOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
} from "@ant-design/icons"
import { LabelItem, LabelType } from "utils/labelUtils"

interface ListPickerModalProps {
  /** Whether the modal is visible */
  isVisible: boolean
  /** Function to close the modal */
  onClose: () => void
  /** Available lists to choose from */
  availableLists: LabelItem[]
  /** Callback when a list is selected */
  onSelectList: (list: LabelItem) => void
  /** Title for the modal */
  title?: string
  /** Description text */
  description?: string
}

/**
 * Get icon based on list type
 */
const getListIcon = (type: LabelType) => {
  switch (type) {
    case LabelType.RANGE_LIST:
      return <OrderedListOutlined style={{ color: "#E67E22" }} />
    case LabelType.LIST:
      return <UnorderedListOutlined style={{ color: "#E67E22" }} />
    default:
      return <UnorderedListOutlined />
  }
}

/**
 * Get badge for list usage count
 */
const getUsageBadge = (list: LabelItem) => {
  const usageCount = list.referenceLineNumbers?.length || 0
  if (usageCount === 0) {
    return <Tag color="default">Unused</Tag>
  }
  return (
    <Tag color="blue">
      {usageCount} usage{usageCount > 1 ? "s" : ""}
    </Tag>
  )
}

/**
 * ListPickerModal Component
 */
const ListPickerModal: React.FC<ListPickerModalProps> = ({
  isVisible,
  onClose,
  availableLists,
  onSelectList,
  title = "Select a List",
  description = "Choose a list to insert into your question",
}) => {
  const [searchTerm, setSearchTerm] = useState("")

  // Filter lists based on search term
  const filteredLists = useMemo(() => {
    if (!searchTerm) return availableLists

    const lowerSearch = searchTerm.toLowerCase()
    return availableLists.filter(
      (list) =>
        list.name.toLowerCase().includes(lowerSearch) ||
        list.content.toLowerCase().includes(lowerSearch)
    )
  }, [availableLists, searchTerm])

  // Handle list selection
  const handleSelectList = (list: LabelItem) => {
    onSelectList(list)
    setSearchTerm("") // Reset search
    onClose()
  }

  // Handle modal close
  const handleClose = () => {
    setSearchTerm("") // Reset search
    onClose()
  }

  return (
    <Modal
      title={title}
      open={isVisible}
      onCancel={handleClose}
      footer={null}
      width={600}
      destroyOnHidden
    >
      <div className="mb-4">
        <p className="text-gray-600 text-sm mb-3">{description}</p>
        <Input
          placeholder="Search lists..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          autoFocus
        />
      </div>

      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {filteredLists.length === 0 ? (
          <Empty
            description={
              searchTerm
                ? `No lists found matching "${searchTerm}"`
                : "No lists available"
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            dataSource={filteredLists}
            renderItem={(list) => (
              <List.Item
                key={list.id}
                className="cursor-pointer hover:bg-gray-50 transition-colors px-3 py-2 rounded"
                onClick={() => handleSelectList(list)}
                style={{ cursor: "pointer" }}
              >
                <List.Item.Meta
                  avatar={getListIcon(list.type)}
                  title={
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{list.name}</span>
                      {getUsageBadge(list)}
                    </div>
                  }
                  description={
                    <div className="text-xs text-gray-500">
                      <div className="mb-1">
                        <Tag
                          color={
                            list.type === LabelType.RANGE_LIST
                              ? "orange"
                              : "blue"
                          }
                        >
                          {list.type}
                        </Tag>
                        <span className="ml-2">
                          {list.originalLines.length > 1
                            ? `${list.originalLines.length - 1} items`
                            : "Empty list"}
                        </span>
                      </div>
                      {list.originalLines.length > 1 && (
                        <div className="mt-1 text-gray-400 truncate">
                          Preview: {list.originalLines.slice(1, 3).join(", ")}
                          {list.originalLines.length > 3 && "..."}
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>

      <div className="mt-4 text-xs text-gray-500 text-center">
        Click on a list to insert it into your question
      </div>
    </Modal>
  )
}

export default ListPickerModal
